import { useMutation, useQuery } from "@apollo/client";
import { PlusIcon, XIcon } from "@heroicons/react/outline";
import { compact, sortBy, uniqBy } from "lodash";
import {
  GetGoalGrowthConnectionsQueryQuery,
  GetGoalGrowthConnectionsQueryQueryVariables,
  GoalArtifactSidebarFragmentFragment,
  GoalCompetencyFragmentFragment,
  GoalRoleFragmentFragment,
  UpdateGoalGrowthConnectionsMutationMutation,
  UpdateGoalGrowthConnectionsMutationMutationVariables,
} from "types/graphql-schema";

import getCareerRoleRelatedDataQuery from "@apps/profile/career-tab/graphql/get-career-role-related-data-query";
import useLabel from "@apps/use-label/use-label";
import Button, { buttonTheme } from "@components/button/button";
import Dropdown, { DropdownOptionType } from "@components/dropdown/dropdown";
import AppLink from "@components/link/link";
import Loading from "@components/loading/loading";
import { Tooltip } from "@components/tooltip/tooltip";
import { assertEdgesNonNull } from "@helpers/helpers";

import getGoalGrowthConnectionsQuery from "../graphql/get-goal-growth-connections-query";
import updateGoalGrowthConnectionsMutation from "../graphql/update-goal-growth-connections-mutation";

export enum GrowthConnectionType {
  Competency = "competency",
  Responsibility = "responsibility",
  FutureRole = "futureRole",
}

const GoalOwnerRoleCompetency = ({
  role,
  goalId,
  competency,
  goalCompetencyRoleItemIds,
}: {
  role: GoalRoleFragmentFragment;
  goalId: number;
  competency: GoalCompetencyFragmentFragment;
  goalCompetencyRoleItemIds: { competencyId: number; roleId: number }[];
}) => {
  const label = useLabel();
  const [updateGoalGrowthConnections, { loading: loadingMutation }] =
    useMutation<
      UpdateGoalGrowthConnectionsMutationMutation,
      UpdateGoalGrowthConnectionsMutationMutationVariables
    >(updateGoalGrowthConnectionsMutation);

  const handleRemoveCompetency = () => {
    const newCompetencyIds = goalCompetencyRoleItemIds.filter(
      (item) => item.competencyId !== competency.id || item.roleId !== role.id
    );
    updateGoalGrowthConnections({
      variables: {
        artifactId: goalId,
        additionalFields: { competencyIds: newCompetencyIds },
      },

      refetchQueries: [getCareerRoleRelatedDataQuery],
    });
  };
  return (
    <div
      className="flex items-center gap-2 py-0.5 px-2 rounded bg-gray-100"
      key={competency.id}
    >
      {competency.title}
      {loadingMutation ? (
        <Loading mini size={3} />
      ) : (
        <Tooltip text={`Remove ${label("competency")}`}>
          <button
            className="text-gray-400 hover:text-gray-600"
            onClick={handleRemoveCompetency}
            disabled={loadingMutation}
          >
            <XIcon className="w-3 h-3" />
          </button>
        </Tooltip>
      )}
    </div>
  );
};

const GoalRoleGroup = ({
  role,
  goalOwnerId,
  competencies,
  goalId,
  goalCompetencyRoleItemIds,
}: {
  role: GoalRoleFragmentFragment;
  goalOwnerId: number;
  competencies: GoalCompetencyFragmentFragment[];
  goalId: number;
  goalCompetencyRoleItemIds: { competencyId: number; roleId: number }[];
}) => {
  if (competencies.length === 0 || !role) {
    return null;
  }
  return (
    <div key={role.id}>
      <div className="text-gray-500">
        <AppLink
          to={`/profile/${goalOwnerId}/career/?careerTrackId=${role.careerTrack.id}&roleId=${role.id}`}
          className="hover:underline"
        >
          {role.title} (Level: {role.level})
        </AppLink>
      </div>
      <div className="mt-3 flex gap-2">
        {competencies.map((competency) => (
          <GoalOwnerRoleCompetency
            key={competency.id}
            role={role}
            goalId={goalId}
            competency={competency}
            goalCompetencyRoleItemIds={goalCompetencyRoleItemIds}
          />
        ))}
      </div>
    </div>
  );
};

const GoalGrowthConnectionList = ({
  goalArtifact,
  isReadOnly = false,
}: {
  goalArtifact: GoalArtifactSidebarFragmentFragment;
  isReadOnly?: boolean;
}) => {
  const label = useLabel();
  const goalOwner = goalArtifact?.owners?.edges[0]?.node;
  const { data, loading: loadingQuery } = useQuery<
    GetGoalGrowthConnectionsQueryQuery,
    GetGoalGrowthConnectionsQueryQueryVariables
  >(getGoalGrowthConnectionsQuery, {
    skip: !goalOwner,
    variables: { artifactId: goalArtifact.id, userId: goalOwner?.id ?? 0 },
  });
  const goal =
    data?.artifact && data.artifact.__typename === "GoalArtifactNode"
      ? data.artifact
      : null;

  const [updateGoalGrowthConnections, { loading: loadingMutation }] =
    useMutation<
      UpdateGoalGrowthConnectionsMutationMutation,
      UpdateGoalGrowthConnectionsMutationMutationVariables
    >(updateGoalGrowthConnectionsMutation);

  const goalCompetencies = goal?.competencyRelations
    ? assertEdgesNonNull(goal.competencyRelations)
    : [];
  const roles = uniqBy(
    goalCompetencies.map((relation) => relation.role).filter((role) => !!role),
    "id"
  );
  const rolesWithCompetencies = sortBy(
    roles.map((role) => ({
      orderValue: `${role.careerTrack.id}-${role.level}`,
      role,
      careerTrack: role.careerTrack,
      competencies: goalCompetencies
        .filter((relation) => relation.role?.id === role.id)
        .map((relation) => relation.competency),
    })),
    "orderValue"
  );

  const goalCompetencyRoleItemIds = goalCompetencies
    .map((relation) =>
      !!relation.role
        ? {
            competencyId: relation.competency.id,
            roleId: relation.role.id,
          }
        : null
    )
    .filter((item) => !!item);

  const goalOwnerRoles = sortBy(
    (data?.user?.currentRoles ? data.user.currentRoles : []).map((role) => ({
      orderValue: `${role.careerTrack.id}-${role.level}`,
      ...role,
    })),
    "orderValue"
  );

  const handleAddCompetency = (roleId: number, competencyId: number) => {
    const newRelation = { competencyId, roleId };
    updateGoalGrowthConnections({
      variables: {
        artifactId: goalArtifact.id,
        additionalFields: {
          competencyIds: [...goalCompetencyRoleItemIds, newRelation],
        },
      },
      refetchQueries: [getCareerRoleRelatedDataQuery],
    });
  };

  if (!goalArtifact.canUpdate.permission || !goalOwner) {
    return null;
  }

  const roleOptions: DropdownOptionType[] = compact(
    goalOwnerRoles.map((role) => {
      const competencies = assertEdgesNonNull(role.competencies).filter(
        (competency) => {
          const hasMatchingCompetencyRole = goalCompetencyRoleItemIds.some(
            (item) =>
              item.competencyId === competency.id && item.roleId === role.id
          );
          return !hasMatchingCompetencyRole;
        }
      );
      return competencies.length > 0
        ? {
            type: "submenu",
            label: `${role.title} (Level: ${role.level})`,
            options: [
              {
                type: "header",
                label: label("competency", {
                  titleCase: true,
                  pluralize: true,
                }),
              },
              ...competencies.map((competency) => ({
                label: competency.title,
                onClick: () => handleAddCompetency(role.id, competency.id),
              })),
            ],
          }
        : null;
    })
  );

  return (
    <div className="w-full flex flex-col gap-2">
      <div className="-mx-3 px-3 flex items-center justify-between">
        <div className="text-sm font-medium">
          Connected {label("competency", { titleCase: true, pluralize: true })}
        </div>
        {!isReadOnly && goalArtifact.canUpdate.permission && (
          <div className="flex items-center gap-2">
            {loadingMutation ? (
              <Loading mini size={5} />
            ) : (
              <Dropdown
                options={[
                  {
                    type: "header",
                    label: "Roles",
                  },
                  ...roleOptions,
                ]}
              >
                <Button
                  icon={PlusIcon}
                  theme={buttonTheme.iconGray}
                  tooltip={
                    roleOptions.length === 0
                      ? `No ${label("competency", {
                          pluralize: true,
                        })} available to connect.`
                      : `Add a ${label("competency")}`
                  }
                  disabled={roleOptions.length === 0}
                />
              </Dropdown>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-4 empty:hidden">
        {loadingQuery && !data ? (
          <Loading>Loading growth connections...</Loading>
        ) : rolesWithCompetencies.length > 0 ? (
          <div className="flex flex-col gap-6 text-sm">
            {rolesWithCompetencies.map(({ role, competencies }) => (
              <GoalRoleGroup
                key={role.id}
                role={role}
                goalOwnerId={goalOwner.id}
                goalId={goalArtifact.id}
                competencies={competencies}
                goalCompetencyRoleItemIds={goalCompetencyRoleItemIds}
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default GoalGrowthConnectionList;
